import type { CalendarItem } from '@/types/contentPlanner';

export const PROCESSING_STATUSES = new Set([
  'pending',
  'in_progress', // the status the content-generation worker actually writes for the whole run
  'generating',
  'analyzing_serp',
  'generating_brief',
  'outline_pending',
  'writing_content',
  'validating',
  'publishing',
]);

export interface BulkActionBuckets {
  publishableItems: CalendarItem[];
  draftableItems: CalendarItem[];
  schedulableItems: CalendarItem[];
  failedItems: CalendarItem[];
}

export function isPublished(item: CalendarItem): boolean {
  return (
    item.status === 'published' ||
    item.wordpress_status === 'publish' ||
    item.publish_status === 'published' ||
    Boolean(item.wp_url)
  );
}

export function isScheduled(item: CalendarItem): boolean {
  return item.publish_status === 'scheduled' || Boolean(item.scheduled_publish_time);
}

export function isCompleted(item: CalendarItem): boolean {
  return item.status === 'completed' || isPublished(item);
}

export function isProcessing(item: CalendarItem): boolean {
  return PROCESSING_STATUSES.has(item.status);
}

export function getBulkActionBuckets(items: CalendarItem[]): BulkActionBuckets {
  return {
    publishableItems: items.filter((item) => isCompleted(item) && !isPublished(item)),
    draftableItems: items.filter((item) => isPublished(item)),
    schedulableItems: items.filter(
      (item) =>
        isCompleted(item) &&
        !isPublished(item) &&
        !isScheduled(item) &&
        !isProcessing(item) &&
        item.status !== 'failed',
    ),
    failedItems: items.filter((item) => item.status === 'failed'),
  };
}

export function tenantForCalendarItem(
  item: CalendarItem,
  fallbackTenant?: string | null,
): string {
  return item._wsTenant || item.tenant_id || fallbackTenant || '';
}

export function groupCalendarItemsByTenant(
  items: CalendarItem[],
  fallbackTenant?: string | null,
): Map<string, CalendarItem[]> {
  const grouped = new Map<string, CalendarItem[]>();
  for (const item of items) {
    const tenantId = tenantForCalendarItem(item, fallbackTenant);
    if (!tenantId) continue;
    const group = grouped.get(tenantId) ?? [];
    group.push(item);
    grouped.set(tenantId, group);
  }
  return grouped;
}
